import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { formatDateIT } from "@/lib/formatDate";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Trash, PencilSimple, Lightning, ClockCounterClockwise } from "@phosphor-icons/react";
import { motion } from "framer-motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusLabels = {
  da_fare: "Da fare",
  in_corso: "In corso",
  completata: "Completata",
};

export default function PhasesTab({ clientId }) {
  const [phases, setPhases] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [statusManuallyChanged, setStatusManuallyChanged] = useState(false);
  const [form, setForm] = useState({
    title: "",
    description: "",
    start_date: "",
    end_date: "",
    status: "da_fare",
    order: 0,
  });

  const fetchPhases = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/clients/${clientId}/phases`);
      setPhases(data);
    } catch {
      // Error handled silently
    }
  }, [clientId]);

  useEffect(() => { fetchPhases(); }, [fetchPhases]);

  const handleSave = async () => {
    try {
      const payload = { ...form };
      if (editing) {
        // If the user changed the status manually, send status_override: true
        if (statusManuallyChanged) {
          payload.status_override = true;
        }
        await axios.put(`${API}/clients/${clientId}/phases/${editing.id}`, payload);
        toast.success("Fase aggiornata");
      } else {
        await axios.post(`${API}/clients/${clientId}/phases`, { ...payload, order: phases.length });
        toast.success("Fase creata");
      }
      setShowDialog(false);
      setEditing(null);
      setStatusManuallyChanged(false);
      setForm({ title: "", description: "", start_date: "", end_date: "", status: "da_fare", order: 0 });
      fetchPhases();
    } catch (err) {
      toast.error("Errore nel salvataggio");
    }
  };

  const handleDelete = async (phaseId) => {
    if (!window.confirm("Eliminare questa fase?")) return;
    try {
      await axios.delete(`${API}/clients/${clientId}/phases/${phaseId}`);
      toast.success("Fase eliminata");
      fetchPhases();
    } catch (err) {
      toast.error("Errore nell'eliminazione");
    }
  };

  const openEdit = (phase) => {
    setEditing(phase);
    setStatusManuallyChanged(false);
    setForm({
      title: phase.title,
      description: phase.description || "",
      start_date: phase.start_date || "",
      end_date: phase.end_date || "",
      status: phase.status || "da_fare",
      order: phase.order || 0,
    });
    setShowDialog(true);
  };

  const handleStatusChange = (v) => {
    setStatusManuallyChanged(true);
    setForm({ ...form, status: v });
  };

  const isAutoStatus = (phase) => {
    return phase.start_date && phase.end_date && !phase.status_override;
  };

  return (
    <div data-testid="phases-tab">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-bold tracking-tight uppercase text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
          FASI DEL PERCORSO
        </h3>
        <Button
          onClick={() => {
            setEditing(null);
            setStatusManuallyChanged(false);
            setForm({ title: "", description: "", start_date: "", end_date: "", status: "da_fare", order: 0 });
            setShowDialog(true);
          }}
          className="bg-[#007AFF] hover:bg-[#3395FF] text-white"
          data-testid="add-phase-button"
        >
          <Plus size={18} className="mr-2" /> Nuova Fase
        </Button>
      </div>

      {phases.length > 0 ? (
        <div className="space-y-4">
          {phases.map((phase, i) => (
            <motion.div
              key={phase.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
              className="bg-[#171717] border border-[#262626] rounded-lg p-5"
              data-testid={`phase-card-${phase.id}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-[#007AFF]/15 flex items-center justify-center">
                    <Lightning size={18} weight="fill" className="text-[#007AFF]" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-white">{phase.title}</h4>
                    {phase.description && <p className="text-sm text-zinc-500 mt-0.5">{phase.description}</p>}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-1.5">
                    <span className={`px-2.5 py-1 rounded text-xs font-medium phase-badge-${phase.status}`} data-testid={`phase-status-badge-${phase.id}`}>
                      {statusLabels[phase.status] || phase.status}
                    </span>
                    {isAutoStatus(phase) && (
                      <span className="text-[9px] text-zinc-600 flex items-center gap-0.5" title="Stato calcolato automaticamente dalle date">
                        <ClockCounterClockwise size={10} /> auto
                      </span>
                    )}
                  </div>
                  <button onClick={() => openEdit(phase)} className="text-zinc-600 hover:text-[#007AFF] transition-colors" data-testid={`edit-phase-${phase.id}`}>
                    <PencilSimple size={16} />
                  </button>
                  <button onClick={() => handleDelete(phase.id)} className="text-zinc-600 hover:text-red-400 transition-colors" data-testid={`delete-phase-${phase.id}`}>
                    <Trash size={16} />
                  </button>
                </div>
              </div>
              {(phase.start_date || phase.end_date) && (
                <div className="flex gap-4 mt-3 text-xs text-zinc-500">
                  {phase.start_date && <span>Inizio: {formatDateIT(phase.start_date)}</span>}
                  {phase.end_date && <span>Fine: {formatDateIT(phase.end_date)}</span>}
                </div>
              )}
            </motion.div>
          ))}
        </div>
      ) : (
        <div className="text-center py-16 bg-[#171717] border border-[#262626] rounded-lg">
          <Lightning size={40} weight="duotone" className="text-zinc-600 mx-auto mb-3" />
          <p className="text-zinc-400">Nessuna fase creata</p>
          <p className="text-zinc-600 text-sm mt-1">Definisci le fasi del percorso di allenamento</p>
        </div>
      )}

      {/* Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="bg-[#171717] border-[#262626] text-white max-w-md" data-testid="phase-dialog">
          <DialogHeader>
            <DialogTitle className="font-bold tracking-tight uppercase" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              {editing ? "MODIFICA FASE" : "NUOVA FASE"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Titolo</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1 bg-[#0A0A0A] border-[#262626] text-white" data-testid="phase-title" />
            </div>
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Descrizione</Label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1 bg-[#0A0A0A] border-[#262626] text-white min-h-[80px] resize-none" data-testid="phase-description" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-zinc-400 text-xs uppercase tracking-wider">Data Inizio</Label>
                <Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="mt-1 bg-[#0A0A0A] border-[#262626] text-white" data-testid="phase-start-date" />
              </div>
              <div>
                <Label className="text-zinc-400 text-xs uppercase tracking-wider">Data Fine</Label>
                <Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="mt-1 bg-[#0A0A0A] border-[#262626] text-white" data-testid="phase-end-date" />
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label className="text-zinc-400 text-xs uppercase tracking-wider">Stato</Label>
                {form.start_date && form.end_date && !statusManuallyChanged && editing && !editing.status_override && (
                  <span className="text-[10px] text-zinc-600 flex items-center gap-1">
                    <ClockCounterClockwise size={10} /> Calcolato dalle date
                  </span>
                )}
              </div>
              <Select value={form.status} onValueChange={handleStatusChange}>
                <SelectTrigger className="mt-1 bg-[#0A0A0A] border-[#262626] text-white" data-testid="phase-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#171717] border-[#262626]">
                  <SelectItem value="da_fare" className="text-white">Da fare</SelectItem>
                  <SelectItem value="in_corso" className="text-white">In corso</SelectItem>
                  <SelectItem value="completata" className="text-white">Completata</SelectItem>
                </SelectContent>
              </Select>
              {statusManuallyChanged && (
                <p className="text-[10px] text-[#FF9500] mt-1">Lo stato verrà forzato manualmente (non più automatico dalle date)</p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)} className="border-[#262626] text-zinc-300 hover:bg-[#1A1A1A]" data-testid="phase-cancel">Annulla</Button>
            <Button onClick={handleSave} className="bg-[#007AFF] hover:bg-[#3395FF] text-white" data-testid="phase-save">{editing ? "Aggiorna" : "Crea"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
